import { useRef, useState, type FormEvent } from 'react';
import { Building2, Hash, ImageUp, Info, Stamp, X } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { useToast } from '../components/ui/useToast';
import { ErreurApi } from '../lib/api';
import { detailsRefus } from '../lib/erreurs-api';
import {
  urlCachetCertificat,
  useDeposerCachet,
  useEmetteur,
  useReglageCertificat,
  useReglerCertificat,
  useReglerEmetteur,
  useReglerSerieFacturation,
  useRetirerCachet,
  useSeriesFacturation,
  type Emetteur,
  type ReglageCertificat,
  type SaisieEmetteur,
  type SaisieReglageCertificat,
  type SerieFacturation,
} from '../lib/useFacturation';

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

function messageRefus(erreur: unknown): string {
  const detail = detailsRefus(erreur)[0];
  if (detail) return detail.message;
  return erreur instanceof ErreurApi ? erreur.message : 'Le réglage n’a pas pu être enregistré.';
}

function dateFrancaise(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Indian/Reunion',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));
}

/**
 * Paramètres de facturation (phase 4) : la numérotation des factures et des
 * avoirs que la plateforme émet, les mentions de l'émetteur qu'elle ne
 * connaît pas (TF-407), et ce que chaque certificat de réalisation reprend —
 * signataire, lieu, cachet et signature (TF-412). Pôle facturation et
 * direction.
 */
export function FacParametresPage(): JSX.Element {
  const series = useSeriesFacturation();
  const emetteur = useEmetteur();
  const certificats = useReglageCertificat();

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Paramètres de facturation"
        sousTitre="Pôle facturation · numérotation et mentions des factures et des avoirs émis par la plateforme"
      />

      <Carte
        titre="Mentions de l’émetteur"
        actions={
          emetteur.data ? (
            <Pastille
              texte={emetteur.data.complet ? 'Prêtes' : 'À régler avant d’émettre'}
              ton={emetteur.data.complet ? 'success' : 'warning'}
            />
          ) : undefined
        }
        sansBordure
      >
        <div className="p-4 sm:p-6">
          {emetteur.isLoading && <Chargement />}
          {emetteur.isError && <MessageErreur message="Les mentions de l’émetteur n’ont pas pu être lues." />}
          {emetteur.data && (
            <FormulaireEmetteur
              key={`${emetteur.data.regleLe ?? ''}`}
              emetteur={emetteur.data}
            />
          )}
        </div>
      </Carte>

      <Carte
        titre="Certificats de réalisation"
        actions={
          certificats.data ? (
            <Pastille
              texte={certificats.data.cachet ? 'Cachet déposé' : 'Cadre à signer à la main'}
              ton={certificats.data.cachet ? 'success' : 'neutral'}
            />
          ) : undefined
        }
        sansBordure
      >
        <div className="p-4 sm:p-6">
          {certificats.isLoading && <Chargement />}
          {certificats.isError && <MessageErreur message="Le réglage des certificats n’a pas pu être lu." />}
          {certificats.data && (
            <ReglageDesCertificats key={`${certificats.data.regleLe ?? ''}`} reglage={certificats.data} />
          )}
        </div>
      </Carte>

      <Carte titre="Numérotation" sansBordure>
        <div className="space-y-4 p-4 sm:p-6">
          <p className="flex gap-2 rounded-xl bg-codialis-sand px-3 py-2.5 text-xs text-codialis-gray">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
            <span className="min-w-0">
              Chaque pièce reçoit son numéro à l’émission, à la suite de sa série : la numérotation
              reste continue, sans trou ni doublon. Pour reprendre une numérotation tenue jusqu’ici
              dans un autre logiciel, indiquez le dernier numéro qu’il a attribué.
            </span>
          </p>

          {series.isLoading && <Chargement />}
          {series.isError && (
            <MessageErreur message="Les séries de numérotation n’ont pas pu être lues." />
          )}
          {series.data && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {series.data.map((serie) => (
                <FormulaireSerie
                  // Un réglage enregistré revient de l'API : le formulaire repart
                  // de ce qu'elle a retenu (préfixe en capitales, etc.).
                  key={`${serie.nature}-${serie.regleeLe ?? ''}-${serie.prefixe}-${serie.dernierNumero}-${serie.longueurRang}`}
                  serie={serie}
                />
              ))}
            </div>
          )}
        </div>
      </Carte>
    </div>
  );
}

function FormulaireSerie({ serie }: { serie: SerieFacturation }): JSX.Element {
  const toast = useToast();
  const regler = useReglerSerieFacturation();
  const [prefixe, setPrefixe] = useState(serie.prefixe);
  const [dernierNumero, setDernierNumero] = useState(String(serie.dernierNumero));
  const [longueurRang, setLongueurRang] = useState(String(serie.longueurRang));
  const [remiseAZeroAnnuelle, setRemiseAZeroAnnuelle] = useState(serie.remiseAZeroAnnuelle);
  const [erreur, setErreur] = useState<string | null>(null);

  const entier = (texte: string): number | null => (/^\d+$/.test(texte.trim()) ? Number(texte.trim()) : null);
  const numero = entier(dernierNumero);
  const chiffres = entier(longueurRang);
  const modifie =
    prefixe.trim().toUpperCase() !== serie.prefixe ||
    numero !== serie.dernierNumero ||
    chiffres !== serie.longueurRang ||
    remiseAZeroAnnuelle !== serie.remiseAZeroAnnuelle;
  const idPrefixe = `prefixe-${serie.nature}`;
  const idNumero = `numero-${serie.nature}`;
  const idChiffres = `chiffres-${serie.nature}`;
  const idRemise = `remise-${serie.nature}`;

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (numero === null) {
      setErreur('Le dernier numéro attribué est un nombre entier, 0 si la série n’a pas servi.');
      return;
    }
    if (chiffres === null) {
      setErreur('Le nombre de chiffres du numéro est un nombre entier.');
      return;
    }
    setErreur(null);
    const reglage = { prefixe: prefixe.trim(), dernierNumero: numero, remiseAZeroAnnuelle, longueurRang: chiffres };
    regler.mutate(
      { nature: serie.nature, reglage },
      {
        onSuccess: () => toast(`Numérotation des ${serie.libelle.toLowerCase()} enregistrée.`),
        onError: (refus) => setErreur(messageRefus(refus)),
      },
    );
  };

  return (
    <form
      onSubmit={enregistrer}
      className="min-w-0 space-y-4 rounded-2xl border border-gray-100 bg-white p-4 sm:p-5"
      aria-labelledby={`titre-${serie.nature}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 id={`titre-${serie.nature}`} className="font-display font-bold text-codialis-navy">
          {serie.libelle}
        </h3>
        <span className="inline-flex min-w-0 items-center gap-1.5 rounded-lg bg-codialis-navy/5 px-2.5 py-1 text-xs text-codialis-navy">
          <Hash className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="text-codialis-gray">Prochain numéro</span>
          <span className="break-all font-mono font-bold">{serie.prochainNumero}</span>
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <label htmlFor={idPrefixe} className="mb-1 block text-xs font-semibold text-codialis-navy">
            Préfixe
          </label>
          <input
            id={idPrefixe}
            value={prefixe}
            onChange={(e) => {
              setPrefixe(e.target.value.toUpperCase());
              setErreur(null);
            }}
            maxLength={10}
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            className={`${CLASSE_CHAMP} font-mono uppercase`}
          />
          <p className="mt-1 text-[11px] text-codialis-gray">
            Facultatif. Une lettre d’abord, puis lettres et chiffres, dix au plus.
          </p>
        </div>
        <div className="min-w-0">
          <label htmlFor={idChiffres} className="mb-1 block text-xs font-semibold text-codialis-navy">
            Chiffres du numéro
          </label>
          <input
            id={idChiffres}
            value={longueurRang}
            onChange={(e) => {
              setLongueurRang(e.target.value);
              setErreur(null);
            }}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            required
            className={`${CLASSE_CHAMP} font-mono`}
          />
          <p className="mt-1 text-[11px] text-codialis-gray">Complété par des zéros : 6 donne 000597.</p>
        </div>
        <div className="min-w-0 sm:col-span-2">
          <label htmlFor={idNumero} className="mb-1 block text-xs font-semibold text-codialis-navy">
            Dernier numéro attribué
          </label>
          <input
            id={idNumero}
            value={dernierNumero}
            onChange={(e) => {
              setDernierNumero(e.target.value);
              setErreur(null);
            }}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            required
            className={`${CLASSE_CHAMP} font-mono`}
          />
          <p className="mt-1 text-[11px] text-codialis-gray">
            {remiseAZeroAnnuelle ? 'Dans l’année en cours.' : 'Depuis le début de la série.'}
          </p>
        </div>
      </div>

      <label
        htmlFor={idRemise}
        className="relative flex min-h-[44px] cursor-pointer items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2"
      >
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-codialis-navy">
            Repartir de 1 chaque année
          </span>
          <span className="block text-[11px] text-codialis-gray">
            {remiseAZeroAnnuelle
              ? 'L’année figure dans le numéro, et la série repart de 1 au 1er janvier.'
              : 'Numérotation continue, sans année dans le numéro.'}
          </span>
        </span>
        <input
          id={idRemise}
          type="checkbox"
          role="switch"
          checked={remiseAZeroAnnuelle}
          onChange={(e) => {
            setRemiseAZeroAnnuelle(e.target.checked);
            setErreur(null);
          }}
          className="peer sr-only"
        />
        <span
          aria-hidden
          className={`flex h-6 w-10 shrink-0 items-center rounded-full px-1 transition peer-focus-visible:ring-2 peer-focus-visible:ring-codialis-sky ${
            remiseAZeroAnnuelle ? 'bg-codialis-teal' : 'bg-gray-300'
          }`}
        >
          <span
            className="h-4 w-4 rounded-full bg-white transition"
            style={{ transform: remiseAZeroAnnuelle ? 'translateX(16px)' : 'translateX(0)' }}
          />
        </span>
      </label>

      <div className="space-y-1 text-xs text-codialis-gray">
        <div>
          {serie.dernierNumeroEmis ? (
            <>
              Dernier numéro émis :{' '}
              <span className="font-mono font-semibold text-codialis-navy">
                {serie.dernierNumeroEmis}
              </span>{' '}
              — le dernier numéro attribué ne peut pas redescendre sous {serie.plancher}.
            </>
          ) : (
            'Aucune pièce émise sous ce réglage.'
          )}
        </div>
        {serie.regleeLe && (
          <div>
            Réglée le {dateFrancaise(serie.regleeLe)}
            {serie.regleePar ? ` par ${serie.regleePar}` : ''}.
          </div>
        )}
      </div>

      {erreur && (
        <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
          {erreur}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {modifie && <Pastille texte="Modifié, non enregistré" ton="warning" />}
        <BoutonPrincipal type="submit" disabled={!modifie || regler.isPending}>
          {regler.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </BoutonPrincipal>
      </div>
    </form>
  );
}

function FormulaireEmetteur({ emetteur }: { emetteur: Emetteur }): JSX.Element {
  const toast = useToast();
  const regler = useReglerEmetteur();
  const [saisie, setSaisie] = useState<SaisieEmetteur>({
    mentionsJuridiques: emetteur.mentionsJuridiques ?? '',
    numeroTva: emetteur.numeroTva ?? '',
    iban: emetteur.iban ?? '',
    bic: emetteur.bic ?? '',
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const { identite } = emetteur;

  const champ = (cle: keyof SaisieEmetteur) => ({
    id: `emetteur-${cle}`,
    value: saisie[cle],
    onChange: (evenement: { target: { value: string } }) => {
      setSaisie((avant) => ({ ...avant, [cle]: evenement.target.value }));
      setErreur(null);
    },
  });

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    regler.mutate(saisie, {
      onSuccess: () => toast('Mentions de l’émetteur enregistrées.'),
      onError: (refus) => setErreur(messageRefus(refus)),
    });
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
      <form onSubmit={enregistrer} className="min-w-0 space-y-4 lg:col-span-3" aria-label="Mentions de l’émetteur">
        {emetteur.detail && (
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">{emetteur.detail}</p>
        )}
        <div className="min-w-0">
          <label htmlFor="emetteur-mentionsJuridiques" className="mb-1 block text-xs font-semibold text-codialis-navy">
            Forme juridique, capital et RCS
          </label>
          <input {...champ('mentionsJuridiques')} maxLength={300} className={CLASSE_CHAMP} />
          <p className="mt-1 text-[11px] text-codialis-gray">
            Recopiés du Kbis : forme juridique, capital social, « RCS », ville du greffe et SIREN. Imprimés tels
            quels sous le nom de l’organisme. Exigés avant toute émission.
          </p>
        </div>
        <div className="min-w-0">
          <label htmlFor="emetteur-numeroTva" className="mb-1 block text-xs font-semibold text-codialis-navy">
            N° de TVA intracommunautaire
          </label>
          <input
            {...champ('numeroTva')}
            maxLength={20}
            autoComplete="off"
            spellCheck={false}
            className={`${CLASSE_CHAMP} font-mono uppercase`}
          />
          <p className="mt-1 text-[11px] text-codialis-gray">
            Facultatif : imprimé s’il est connu. FR, deux chiffres de clé, puis le SIREN.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="min-w-0 sm:col-span-2">
            <label htmlFor="emetteur-iban" className="mb-1 block text-xs font-semibold text-codialis-navy">
              IBAN
            </label>
            <input
              {...champ('iban')}
              maxLength={42}
              autoComplete="off"
              spellCheck={false}
              className={`${CLASSE_CHAMP} font-mono uppercase`}
            />
          </div>
          <div className="min-w-0">
            <label htmlFor="emetteur-bic" className="mb-1 block text-xs font-semibold text-codialis-navy">
              BIC
            </label>
            <input
              {...champ('bic')}
              maxLength={15}
              autoComplete="off"
              spellCheck={false}
              className={`${CLASSE_CHAMP} font-mono uppercase`}
            />
          </div>
        </div>
        <p className="text-[11px] text-codialis-gray">
          Les coordonnées bancaires s’impriment sous « Règlement par virement ». Les pièces déjà émises
          n’en changent pas : leur original est figé.
        </p>

        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="min-w-0 text-xs text-codialis-gray">
            {emetteur.regleLe
              ? `Réglées le ${dateFrancaise(emetteur.regleLe)}${emetteur.reglePar ? ` par ${emetteur.reglePar}` : ''}.`
              : 'Jamais réglées.'}
          </p>
          <BoutonPrincipal type="submit" disabled={regler.isPending}>
            {regler.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </BoutonPrincipal>
        </div>
      </form>

      <aside className="min-w-0 space-y-2 rounded-2xl bg-codialis-sand p-4 text-sm lg:col-span-2" aria-label="Identité de l’organisme">
        <p className="flex items-center gap-2 font-display font-bold text-codialis-navy">
          <Building2 className="h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
          Déjà connu de la plateforme
        </p>
        <p className="text-codialis-navy">
          <span className="font-semibold">{identite.nom}</span>
          <span className="block text-xs text-codialis-gray">Siège social : {identite.siege}</span>
          <span className="block text-xs text-codialis-gray">
            Déclaration d’activité n° {identite.numeroDeclarationActivite}
          </span>
        </p>
        <ul className="space-y-1 text-xs">
          {identite.antennes.map((antenne) => (
            <li key={antenne.code} className="flex flex-wrap justify-between gap-x-3 text-codialis-navy">
              <span className="min-w-0">{antenne.nom}</span>
              <span className="break-all font-mono">SIRET {antenne.siret}</span>
            </li>
          ))}
        </ul>
        <p className="text-[11px] text-codialis-gray">
          Chaque pièce porte le SIRET de l’antenne du dossier, celui du CFA au CERFA.
        </p>
      </aside>
    </div>
  );
}

/**
 * Ce que chaque certificat de réalisation reprend (TF-412) : le signataire et
 * le lieu, et l'image du cachet et de la signature imprimée dans le cadre du
 * modèle. Sans image, le cadre reste vide et se signe à la main.
 */
function ReglageDesCertificats({ reglage }: { reglage: ReglageCertificat }): JSX.Element {
  const toast = useToast();
  const regler = useReglerCertificat();
  const deposer = useDeposerCachet();
  const retirer = useRetirerCachet();
  const champFichier = useRef<HTMLInputElement>(null);
  const [saisie, setSaisie] = useState<SaisieReglageCertificat>({
    signataireNom: reglage.signataireNom,
    signataireQualite: reglage.signataireQualite,
    lieuSignature: reglage.lieuSignature,
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [erreurCachet, setErreurCachet] = useState<string | null>(null);
  const modifie =
    saisie.signataireNom.trim() !== reglage.signataireNom ||
    saisie.signataireQualite.trim() !== reglage.signataireQualite ||
    saisie.lieuSignature.trim() !== reglage.lieuSignature;

  const champ = (cle: keyof SaisieReglageCertificat) => ({
    id: `certificats-${cle}`,
    value: saisie[cle],
    onChange: (evenement: { target: { value: string } }) => {
      setSaisie((avant) => ({ ...avant, [cle]: evenement.target.value }));
      setErreur(null);
    },
  });

  const enregistrer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    regler.mutate(saisie, {
      onSuccess: () => toast('Réglage des certificats enregistré.'),
      onError: (refus) => setErreur(messageRefus(refus)),
    });
  };

  const choisir = (fichier: File | null): void => {
    if (!fichier) return;
    setErreurCachet(null);
    deposer.mutate(fichier, {
      onSuccess: () => toast('Image du cachet et de la signature déposée.'),
      onError: (refus) => setErreurCachet(messageRefus(refus)),
      onSettled: () => {
        if (champFichier.current) champFichier.current.value = '';
      },
    });
  };

  const retirerCachet = (): void => {
    setErreurCachet(null);
    retirer.mutate(undefined, {
      onSuccess: () => toast('Image retirée : le cadre se signera à la main.'),
      onError: (refus) => setErreurCachet(messageRefus(refus)),
    });
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
      <form onSubmit={enregistrer} className="min-w-0 space-y-4 lg:col-span-3" aria-label="Signataire des certificats">
        <p className="flex gap-2 rounded-xl bg-codialis-sand px-3 py-2.5 text-xs text-codialis-gray">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
          <span className="min-w-0">
            Repris par chaque certificat de réalisation joint aux factures d’apprentissage, à partir de la
            deuxième échéance. Un certificat déjà produit n’en change pas : il se régénère depuis son échéance.
          </span>
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="min-w-0">
            <label htmlFor="certificats-signataireNom" className="mb-1 block text-xs font-semibold text-codialis-navy">
              Signataire
            </label>
            <input {...champ('signataireNom')} required maxLength={120} className={CLASSE_CHAMP} />
            <p className="mt-1 text-[11px] text-codialis-gray">
              Civilité comprise : « M. MELADE Thierry ». Elle accorde « Je soussigné(e) ».
            </p>
          </div>
          <div className="min-w-0">
            <label
              htmlFor="certificats-signataireQualite"
              className="mb-1 block text-xs font-semibold text-codialis-navy"
            >
              Qualité
            </label>
            <input {...champ('signataireQualite')} required maxLength={120} className={CLASSE_CHAMP} />
          </div>
          <div className="min-w-0">
            <label htmlFor="certificats-lieuSignature" className="mb-1 block text-xs font-semibold text-codialis-navy">
              Fait à
            </label>
            <input {...champ('lieuSignature')} required maxLength={100} className={CLASSE_CHAMP} />
          </div>
        </div>
        {erreur && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreur}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="min-w-0 text-xs text-codialis-gray">
            {reglage.regleLe
              ? `Réglé le ${dateFrancaise(reglage.regleLe)}${reglage.reglePar ? ` par ${reglage.reglePar}` : ''}.`
              : 'Valeurs du modèle remis par TOP FORMATION.'}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {modifie && <Pastille texte="Modifié, non enregistré" ton="warning" />}
            <BoutonPrincipal type="submit" disabled={regler.isPending || (!modifie && !reglage.parDefaut)}>
              {regler.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </BoutonPrincipal>
          </div>
        </div>
      </form>

      <section
        className="min-w-0 space-y-3 rounded-2xl bg-codialis-sand p-4 lg:col-span-2"
        aria-label="Cachet et signature"
      >
        <p className="flex items-center gap-2 font-display font-bold text-codialis-navy">
          <Stamp className="h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
          Cachet et signature
        </p>
        {reglage.cachet ? (
          <>
            <div className="flex min-h-[120px] items-center justify-center rounded-xl border border-gray-200 bg-white p-3">
              <img
                src={`${urlCachetCertificat()}?depot=${encodeURIComponent(reglage.cachet.deposeLe ?? '')}`}
                alt="Cachet et signature imprimés sur les certificats"
                className="max-h-32 max-w-full object-contain"
              />
            </div>
            <p className="text-[11px] text-codialis-gray">
              Imprimée dans le cadre de chaque certificat produit.
              {reglage.cachet.deposeLe
                ? ` Déposée le ${dateFrancaise(reglage.cachet.deposeLe)}${
                    reglage.cachet.deposePar ? ` par ${reglage.cachet.deposePar}` : ''
                  }.`
                : ''}
            </p>
          </>
        ) : (
          <p className="text-xs text-codialis-gray">
            Aucune image : le cadre « Cachet et signature » des certificats reste vide, à signer et tamponner à la
            main.
          </p>
        )}
        <input
          ref={champFichier}
          id="certificats-cachet"
          type="file"
          accept=".png,.jpg,.jpeg,image/png,image/jpeg"
          onChange={(evenement) => choisir(evenement.target.files?.[0] ?? null)}
          // Ouvert par le bouton « Déposer une image » : hors de la tabulation.
          className="hidden"
        />
        {erreurCachet && (
          <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
            {erreurCachet}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <BoutonSecondaire onClick={() => champFichier.current?.click()} disabled={deposer.isPending}>
            <ImageUp className="mr-1.5 h-4 w-4" aria-hidden />
            {deposer.isPending ? 'Dépôt…' : reglage.cachet ? 'Remplacer l’image' : 'Déposer une image'}
          </BoutonSecondaire>
          {reglage.cachet && (
            <BoutonSecondaire onClick={retirerCachet} disabled={retirer.isPending}>
              <X className="mr-1.5 h-4 w-4" aria-hidden />
              Retirer
            </BoutonSecondaire>
          )}
        </div>
        <p className="text-[11px] text-codialis-gray">PNG ou JPEG, 2 Mo au plus, fond blanc ou transparent.</p>
      </section>
    </div>
  );
}
